"use client";

import React, { useId, useRef, useState } from "react";
import clsx from "clsx";
import ScrollReveal from "@/components/common/ScrollReveal";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import {
  ACCORDION_PANEL_BG,
  AccordionChevron,
  accordionBody,
  accordionBodyClosed,
  accordionBodyOpen,
  accordionCard,
  accordionDivider,
  accordionHeader,
  accordionPanel,
  accordionPanelClip,
  accordionPanelClosed,
  accordionPanelOpen,
  useAccordionScrollAnchor,
} from "@/components/common/accordion";

interface TabContentItem {
  id: string;
  /** Tab label. Pass an array to render the label across multiple lines. */
  title: string | string[];
  content: React.ReactNode;
}

interface TabbedContentProps {
  items: TabContentItem[];
  defaultTab?: string;
  currentTab?: string;
  onTabChange?: (id: string) => void;
  /** Accessible name for the tablist / accordion group. */
  label?: string;
  /**
   * Row to have open on mount in the accordion (below `lg`). Omit to keep the
   * default, every row collapsed. Desktop is unaffected either way — there a tab
   * is always selected.
   */
  defaultOpenRow?: string;
}

/**
 * Shared "Core Capabilities" panel used by multiple product and service pages
 * (SHRED, Scarf, LocalRegulatory, …).
 *
 * One DOM tree serves two layouts, switched by CSS only (no hydration flash):
 *
 *   • `< lg` — accordion. Headers and their panels alternate in source order,
 *     each header full width with a chevron; the open row expands between
 *     headers. It starts fully collapsed (or on `defaultOpenRow`), opens one row
 *     at a time, and tapping the open row closes it again. That open row is
 *     tracked separately from `activeTab` so the controlled parents keep their
 *     non-nullable selection for the desktop layout (and for illustrations wired
 *     to the same state).
 *   • `>= lg` — the Figma tab layout. A single-row grid places every header in
 *     row 1 (one equal-width column each) and every panel in row 2 spanning all
 *     columns, so the panels stack in one cell and only the active one has
 *     height.
 *
 * Open/close is `grid-template-rows: 0fr → 1fr` on the panel, which animates on
 * mobile and is instant on desktop (matching the previous show/hide behaviour).
 *
 * Visual design comes from Figma "Core Capabilities" (e.g. node 694-57613):
 *   • Inactive tab: bg #171A1D, white text
 *   • Active tab:   bg #1F2328, primary text (desktop); primary text only on mobile
 *   • Content body: gradient from #15181D to #1F2428, rounded bottom corners
 */
export default function TabbedContent({
  items,
  defaultTab,
  currentTab,
  onTabChange,
  label = "Core capabilities",
  defaultOpenRow,
}: TabbedContentProps) {
  const [internalTab, setInternalTab] = useState(
    defaultTab || items?.[0]?.id || ""
  );
  const isDesktop = useIsDesktop();
  const uid = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Below `lg`, closing the previously open row shifts the tapped header up by
  // the panel height it gives back; this holds it still.
  const anchorRow = useAccordionScrollAnchor();

  // Use controlled or uncontrolled mode
  const activeTab = currentTab !== undefined ? currentTab : internalTab;

  // Which accordion row is open below `lg`. Defaults to null so mobile opens
  // with every row collapsed, independent of the tab the desktop layout selects;
  // `defaultOpenRow` lets a page start with one row already open.
  const [openRow, setOpenRow] = useState<string | null>(defaultOpenRow ?? null);
  const syncedTab = useRef(activeTab);

  // A selection made outside the header row — the SEC/OIC/PDPA circles on Local
  // Regulatory, Scarf's wedges — should reveal that row on mobile too.
  React.useEffect(() => {
    if (activeTab === syncedTab.current) return;
    syncedTab.current = activeTab;
    setOpenRow(activeTab);
  }, [activeTab]);

  const handleTabClick = (id: string) => {
    if (currentTab === undefined) {
      setInternalTab(id);
    }
    onTabChange?.(id);
  };

  const handleHeaderClick = (id: string) => {
    // Mobile: tapping the open row collapses it and leaves the parent's
    // selection alone. Desktop: a tab is always selected.
    if (!isDesktop && openRow === id) {
      setOpenRow(null);
      return;
    }
    setOpenRow(id);
    handleTabClick(id);
  };

  // Update internal state when currentTab changes (for controlled mode)
  React.useEffect(() => {
    if (currentTab !== undefined) {
      setInternalTab(currentTab);
    }
  }, [currentTab]);

  // Arrow-key navigation, only meaningful while the tab layout is showing.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isDesktop) return;

    const current = items.findIndex((item) => item.id === activeTab);
    if (current < 0) return;

    let next = current;
    if (event.key === "ArrowRight") next = (current + 1) % items.length;
    else if (event.key === "ArrowLeft")
      next = (current - 1 + items.length) % items.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = items.length - 1;
    else return;

    event.preventDefault();
    handleTabClick(items[next].id);
    tabRefs.current[items[next].id]?.focus();
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      role={isDesktop ? "tablist" : undefined}
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={clsx(
        accordionCard,
        "grid grid-cols-1 lg:rounded-[12px]",
        // Desktop: one equal-width column per tab; panels span the full row 2.
        "lg:[grid-template-columns:repeat(var(--tab-count),minmax(0,1fr))]"
      )}
      style={{ "--tab-count": items.length } as React.CSSProperties}
    >
      {items.map((item, index) => {
        // `isActive` drives the tab layout, `isOpen` the accordion rows; each is
        // applied through its own breakpoint so one DOM tree covers both.
        const isActive = activeTab === item.id;
        const isOpen = openRow === item.id;
        const lines = Array.isArray(item.title) ? item.title : [item.title];
        const tabId = `${uid}-tab-${item.id}`;
        const panelId = `${uid}-tabpanel-${item.id}`;

        return (
          <React.Fragment key={item.id}>
            {/* Header — accordion row on mobile, tab on desktop. */}
            <ScrollReveal
              direction="up"
              delay={index * 80}
              className={clsx(
                "min-w-0",
                // Separator between accordion rows (dropped in tab layout).
                index > 0 && [accordionDivider, "lg:border-t-0"]
              )}
            >
              <button
                id={tabId}
                ref={(el) => {
                  tabRefs.current[item.id] = el;
                }}
                role={isDesktop ? "tab" : undefined}
                aria-selected={isDesktop ? isActive : undefined}
                aria-expanded={isDesktop ? undefined : isOpen}
                aria-controls={panelId}
                tabIndex={isDesktop && !isActive ? -1 : 0}
                onClick={(event) => {
                  anchorRow(event.currentTarget);
                  handleHeaderClick(item.id);
                }}
                className={clsx(
                  accordionHeader,
                  "h-full",
                  "lg:justify-center lg:gap-0 lg:text-center",
                  "lg:px-5 lg:py-5 lg:min-h-[92px]",
                  "lg:text-[18px] xl:text-[19px] lg:leading-tight",
                  // Mobile: the open row is red. Desktop: the selected tab is.
                  isOpen
                    ? "text-primary"
                    : "text-text-default hover:text-text-highlight",
                  isActive
                    ? "lg:text-primary lg:bg-[#1F2328]"
                    : "lg:text-text-default lg:hover:text-text-default lg:hover:bg-[#1A1D20]"
                )}
              >
                <span className="flex-1 min-w-0 lg:flex-none">
                  {/* Mobile keeps the label on one flowing line; desktop honours
                      the per-line breaks from the Figma labels. */}
                  <span className="lg:hidden">{lines.join(" ")}</span>
                  <span className="hidden lg:block break-words">
                    {lines.map((line, i) => (
                      <span key={i} className="block">
                        {line}
                      </span>
                    ))}
                  </span>
                </span>
                <AccordionChevron open={isOpen} className="lg:hidden" />
              </button>
            </ScrollReveal>

            {/* Panel — expands in place on mobile, sits in row 2 on desktop. */}
            <div
              id={panelId}
              role={isDesktop ? "tabpanel" : "region"}
              aria-labelledby={tabId}
              inert={isDesktop ? !isActive : !isOpen}
              className={clsx(
                accordionPanel,
                "lg:transition-none",
                "lg:[grid-column:1/-1] lg:[grid-row:2]",
                isOpen ? accordionPanelOpen : accordionPanelClosed,
                // The tab layout ignores the accordion row and follows the
                // selected tab instead.
                isActive ? "lg:grid-rows-[1fr]" : "lg:grid-rows-[0fr]"
              )}
              style={{ background: ACCORDION_PANEL_BG }}
            >
              <div className={accordionPanelClip}>
                <div
                  className={clsx(
                    accordionBody,
                    "lg:px-12 lg:py-12 lg:text-[16px] lg:leading-[28px] lg:tracking-[0.5px]",
                    // The copy fades with the row on mobile and with the selected
                    // tab on desktop.
                    isOpen ? accordionBodyOpen : accordionBodyClosed,
                    isActive
                      ? "lg:opacity-100 lg:translate-y-0 lg:delay-0"
                      : "lg:opacity-0 lg:-translate-y-[6px]"
                  )}
                >
                  {item.content}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
